import React, { useState } from "react";
import { EmailContext, WizardAnswers } from "../../types";
import Header from "../components/Header";
import ContextStrip from "../components/ContextStrip";
import WizardQuestion from "../components/WizardQuestion";
import {
  PRIMARY_GOAL_OPTIONS,
  RECEPTIVITY_OPTIONS,
  showsWhiteboard,
} from "../wizardOptions";
import styles from "./WriteEmailView.module.css";

interface WriteEmailViewProps {
  emailContext: EmailContext | null;
  initialWizard?: WizardAnswers | null;
  initialBrief?: string;
  onGenerate: (wizard: WizardAnswers, brief: string) => void;
  onClose: () => void;
  onBack: () => void;
}

export default function WriteEmailView({
  emailContext,
  initialWizard = null,
  initialBrief = "",
  onGenerate,
  onBack,
}: WriteEmailViewProps) {
  const [primaryGoal, setPrimaryGoal] = useState(initialWizard?.primaryGoal ?? "");
  const [receptivity, setReceptivity] = useState(initialWizard?.receptivity ?? "");
  const [whiteboard, setWhiteboard] = useState(initialWizard?.whiteboard ?? "");
  const [brief, setBrief] = useState(initialBrief);

  // A reply has a prior thread ASLAN can draw on; a brand-new email (no thread)
  // has none — so the brief is the only context, and we require it rather than
  // let the model invent the recipient's situation.
  const hasThreadContext = (emailContext?.threadLength ?? 0) > 0;
  const briefRequired = !hasThreadContext;
  const briefFilled = brief.trim().length > 0;

  const ready =
    primaryGoal.length > 0 && receptivity.length > 0 && (!briefRequired || briefFilled);
  const showWhiteboard = showsWhiteboard(primaryGoal);

  const handleGenerate = () => {
    if (!ready) return;
    onGenerate(
      {
        primaryGoal,
        receptivity,
        whiteboard: showWhiteboard ? whiteboard.trim() || undefined : undefined,
      },
      brief,
    );
  };

  const briefBox = (
    <div className={`${styles.briefSection} ${briefRequired ? styles.briefRequired : ""}`}>
      <div className={styles.briefLabel}>
        <span className={styles.briefTitle}>
          {briefRequired ? "What should this email say?" : "Add additional brief"}
        </span>
        {briefRequired ? (
          <span className={styles.required}>required</span>
        ) : (
          <span className={styles.optional}>optional</span>
        )}
      </div>
      <textarea
        value={brief}
        onChange={(e) => setBrief(e.target.value)}
        placeholder="e.g. Confirm Thursday 3pm call, send agenda, keep it warm and concise..."
        className={styles.briefTextarea}
        rows={4}
      />
      <div className={styles.hint}>
        {briefRequired
          ? "This is a new email with no previous thread, so tell ASLAN what to say — it won't invent details about the recipient."
          : "The brief is optional — ASLAN can generate from the thread context and selected prompts. Score runs automatically once the draft is ready."}
      </div>
    </div>
  );

  return (
    <div className={styles.panel}>
      <Header onBack={onBack} />
      <div className={styles.body}>
        <div className={styles.content}>
          <ContextStrip context={emailContext} />

          {briefRequired && briefBox}

          <WizardQuestion
            number={1}
            question="What is the primary goal of this email?"
            placeholder="Select a goal..."
            options={PRIMARY_GOAL_OPTIONS}
            value={primaryGoal}
            onChange={setPrimaryGoal}
          />
          <WizardQuestion
            number={2}
            question="What is the prospect's current level of receptivity?"
            placeholder="Select receptivity..."
            options={RECEPTIVITY_OPTIONS}
            value={receptivity}
            onChange={setReceptivity}
          />

          {showWhiteboard && (
            <div className={styles.briefSection}>
              <div className={styles.briefLabel}>
                <span className={styles.briefTitle}>What&rsquo;s on their whiteboard right now?</span>
                <span className={styles.optional}>optional</span>
              </div>
              <textarea
                value={whiteboard}
                onChange={(e) => setWhiteboard(e.target.value)}
                placeholder="e.g. Q3 pipeline targets, a stalled renewal, a new VP ramping up..."
                className={styles.briefTextarea}
                rows={3}
              />
            </div>
          )}

          {!briefRequired && briefBox}
        </div>

        <div className={styles.footer}>
          <button
            className={`${styles.cta} ${ready ? styles.ctaReady : styles.ctaDisabled}`}
            onClick={handleGenerate}
            disabled={!ready}
            type="button"
          >
            <span>▷ Generate Email</span>
          </button>
          <div className={styles.scoringNote}>
            {ready
              ? "Scored against ASLAN methodology automatically"
              : briefRequired && primaryGoal.length > 0 && receptivity.length > 0
                ? "Add a brief so ASLAN has something to work from"
                : "Select a goal and receptivity to continue"}
          </div>
        </div>
      </div>
    </div>
  );
}
